import { prefersReducedMotion } from '@theme/utilities';

class NumberComponent extends HTMLElement {
  connectedCallback() {
    this.#valueElement = this.querySelector('.number-block__value');

    if (!this.#valueElement) return;

    this.#hasFinished = false;
    this.#hasStarted = false;
    this.#isWaiting = false;

    this.#targetValue = this.#getNumericValue(this.dataset.target, 0);
    this.#decimals = this.#getNumericValue(this.dataset.decimals, 0);
    this.#duration = this.#getNumericValue(this.dataset.duration, 1200);
    this.#delay = this.#getNumericValue(this.dataset.delay, 0);

    this.#renderValue(this.#startValue);
    this.#setObserver();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #observer;
  #valueElement;
  #delayTimeout;
  #rafId;
  #hasFinished = false;
  #hasStarted = false;
  #isWaiting = false;
  #startValue = 0;
  #targetValue = 0;
  #duration = 1200;
  #delay = 0;
  #decimals = 0;

  #setObserver() {
    if (!('IntersectionObserver' in window)) {
      this.#startCounting();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: 0.35 });
    this.#observer.observe(this);
  }

  #handleIntersection = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.#startCounting();
    } else if (this.#isWaiting) {
      this.#cancelDelay();
    }
  };

  #startCounting() {
    if (this.#hasFinished || this.#isWaiting || this.#hasStarted) return;

    this.#isWaiting = true;
    this.classList.remove('is-finished');
    this.classList.remove('is-counting');
    this.#renderValue(this.#startValue);

    this.#delayTimeout = window.setTimeout(() => {
      this.#isWaiting = false;
      this.#hasStarted = true;

      if (prefersReducedMotion()) {
        this.#finish();
        return;
      }

      this.classList.add('is-counting');
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / this.#duration, 1);
        const nextValue = this.#startValue + (this.#targetValue - this.#startValue) * progress;

        this.#renderValue(nextValue);

        if (progress < 1) {
          this.#rafId = requestAnimationFrame(tick);
          return;
        }

        this.#finish();
      };

      this.#rafId = requestAnimationFrame(tick);
    }, this.#delay);
  }

  #finish() {
    this.#renderValue(this.#targetValue);
    this.classList.add('is-finished');
    this.classList.remove('is-counting');
    this.#hasFinished = true;
    this.#cleanupObserver();
  }

  #renderValue(value) {
    const decimals = Math.max(0, Math.round(this.#decimals));
    const clampedValue = decimals > 0 ? Number(value).toFixed(decimals) : Math.round(value).toString();
    const numericValue = Number(clampedValue);

    this.#valueElement.textContent = Number.isNaN(numericValue)
      ? clampedValue
      : numericValue.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
  }

  #cancelPending() {
    this.#cancelDelay();

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = undefined;
    }
  }

  #cancelDelay() {
    if (this.#delayTimeout) {
      clearTimeout(this.#delayTimeout);
      this.#delayTimeout = undefined;
    }

    this.#isWaiting = false;
  }

  #cleanupObserver() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #cleanup() {
    this.#cancelPending();
    this.#cleanupObserver();
    this.#hasStarted = false;
  }

  #getNumericValue(value, fallback = 0) {
    const parsed = Number.parseFloat(value ?? '');
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}

if (!customElements.get('number-component')) {
  customElements.define('number-component', NumberComponent);
}
